import { useContext, useState } from "react";
import useTranslator from "../hooks/useTranslator";
import LandLordContext from "../context/landlord/LandLordContext";
import { toast } from "../helper/common";
import { Badge, Button, Card, Checkbox, Drawer, TextArea } from "./design";
import "./SendTenantEmailDrawer.css";

const SendTenantEmailDrawer = ({
  user,
  isOpen,
  onClose,
  globalTenantDocuments,
  globalAllForensicDocuments,
  currentLang,
  globalTenantDocsHistory,
  globalMandatoryDocsList,
}) => {
  const { _t } = useTranslator();
  const { sendNoteToTenant } = useContext(LandLordContext);
  const [sendNoteLoading, setSendNoteLoading] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isDocumentSelected, setIsDocumentSelected] = useState(false);
  const [note, setNote] = useState("");

  // toggle sections like KYC, Financial Score, Guarantor
  const toggleSection = (value, key) => {
    if (key === "documents") {
      setIsDocumentSelected((prev) => !prev);
      // clear docs if unchecked
      if (isDocumentSelected) {
        setSelectedDocuments([]);
      }
    } else {
      setSelectedSections((prev) =>
        prev.includes(value)
          ? prev.filter((item) => item !== value)
          : [...prev, value]
      );
    }
  };

  // toggle individual documents
  const toggleDocument = (docName) => {
    setSelectedDocuments((prev) =>
      prev.includes(docName)
        ? prev.filter((item) => item !== docName)
        : [...prev, docName]
    );
  };

  const handleSendNote = async () => {
    if (!note) {
      toast.error(_t("pleaseEnterANote"));
      return;
    }
    try {
      setSendNoteLoading(true);

      const finalSelectedItems = [
        ...selectedSections,
        ...(isDocumentSelected ? selectedDocuments : []),
      ];

      const data = {
        tenant_id: user?._id,
        selectedItems: finalSelectedItems,
        note,
      };

      await sendNoteToTenant(data);
      toast.success(
        _t(
          "noteSentSuccessfully",
          "Text used when landlord send mail to tenant"
        )
      );
      onClose();
    } catch (error) {
      toast.error(
        _t("FailedToSendNote", "Text used when landlord send mail to tenant")
      );
    } finally {
      setSendNoteLoading(false);
    }
  };

  const sectionOptions = [
    {
      title: _t("idDocument"),
      subtitle: _t("kycIdentification", "Text used in send note popup"),
    },
    {
      title: _t("financialScore"),
      subtitle: _t("incomeAndStatements", "Text used in send note popup"),
    },
    {
      key: "documents",
      title: _t("documents"),
      subtitle: _t("specificFiles"),
    },
    {
      title: _t("guarantor"),
      subtitle: _t("missingOrIncomplete"),
    },
  ];

  const documentRow = (key, docName, badge) => (
    <Checkbox
      key={key}
      checked={selectedDocuments.includes(docName)}
      onChange={() => toggleDocument(docName)}
      label={
        <span className="sendFeedbackDrawer__doc">
          {docName}
          {badge}
        </span>
      }
    />
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={_t("sendTenantEmailFor")}
      description={_t("sendFeedbackToTheTenantDescription")}
      closeLabel={_t("close")}
      footer={
        <Button
          variant="primary"
          size="lg"
          block
          loading={sendNoteLoading}
          disabled={selectedSections.length === 0 && !isDocumentSelected}
          onClick={handleSendNote}
        >
          {_t("sendViaEmail")}
        </Button>
      }
    >
      <div className="sendFeedbackDrawer">
        {/* Sections */}
        <div className="sendFeedbackDrawer__sections">
          {sectionOptions.map(({ key, title, subtitle }, index) => {
            const checked =
              key === "documents"
                ? isDocumentSelected
                : selectedSections.includes(title);
            return (
              <Card
                as="div"
                key={index}
                className={`sendFeedbackDrawer__option ${checked ? "is-selected" : ""}`}
              >
                <Checkbox
                  checked={checked}
                  onChange={() => toggleSection(title, key)}
                  label={title}
                  description={subtitle}
                />
              </Card>
            );
          })}
        </div>

        {/* Documents */}
        {isDocumentSelected && (
          <Card as="div" title={_t("documents")}>
            <div className="sendFeedbackDrawer__docs">
              {Object.entries(globalMandatoryDocsList)?.map(([key]) => {
                const isPayslipKey = key.toLowerCase().includes("payslip");

                const payslipDocsCount = Object.keys(
                  globalTenantDocsHistory
                ).filter((docKey) =>
                  docKey.toLowerCase().includes("payslip")
                ).length;

                if (isPayslipKey) {
                  const payslipOrder = [
                    "1st month payslip",
                    "2nd month payslip",
                    "3rd month payslip",
                  ];
                  const index = payslipOrder.indexOf(key);

                  if (index < payslipDocsCount) {
                    return null;
                  }
                }

                // normal missing document check
                if (!(key in globalTenantDocsHistory && !isPayslipKey)) {
                  const docName = _t(key, "", "fixed");
                  return documentRow(
                    key,
                    docName,
                    <Badge tone="warning">{_t("missing")}</Badge>
                  );
                }

                return null;
              })}
              {[...new Set(globalTenantDocuments.map((doc) => doc?.type))].map(
                (type, index) => {
                  const doc = globalTenantDocuments.find(
                    (d) => d.type === type
                  );

                  const docName =
                    globalAllForensicDocuments?.[doc?.file_name]
                      ?.localDocumentName?.[
                      currentLang === "en-us" ? "en" : "es"
                    ] ||
                    globalAllForensicDocuments?.[doc?.file_name]
                      ?.documentNameTranslated?.[
                      currentLang === "en-us" ? "en" : "es"
                    ] ||
                    globalAllForensicDocuments?.[doc?.file_name]
                      ?.documentName ||
                    _t(doc?.type, "", "fixed");

                  return documentRow(
                    `uploaded-${index}`,
                    docName,
                    <Badge tone="lime">{_t("uploaded")}</Badge>
                  );
                }
              )}
            </div>
          </Card>
        )}

        {/* Note */}
        <TextArea
          id="send-email-note"
          label={_t("personalNote")}
          placeholder={_t("personalNotePlaceholder")}
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Drawer>
  );
};

export default SendTenantEmailDrawer;
